/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
 import { AjaxError } from 'rxjs/ajax';
 import {
   CumulativeUsageByNetwork,
   UsageByNetwork,
   UsageByLink,
   TotalUsageByNetwork,
   TotalUsageByLink,
   TotalPacketLossByLink,
   ServiceAvailability,
   PacketLossByLink,
 } from '../../shared/entities/dataUsage';
 import { Action, ActionCreators } from './actions';
 
 export interface State {
   totalUsageByNetwork: TotalUsageByNetwork | undefined;
   requestingTotalUsageByNetwork: boolean;
   receivedTotalUsageByNetworkError: AjaxError | undefined;
 
   totalUsageByLink: TotalUsageByLink | undefined;
   requestingTotalUsageByLink: boolean;
   receivedTotalUsageByLinkError: AjaxError | undefined;
 
   usageByLink: UsageByLink | undefined;
   requestingUsageByLink: boolean;
   receivedUsageByLinkError: AjaxError | undefined;
 
   cumulativeUsageByLink: UsageByLink | undefined;
   requestingCumulativeUsageByLink: boolean;
   receivedCumulativeUsageByLinkError: AjaxError | undefined;
 
   usageByNetwork: UsageByNetwork | undefined;
   requestingUsageByNetwork: boolean;
   receivedUsageByNetworkError: AjaxError | undefined;
 
   cumulativeUsageByNetwork: CumulativeUsageByNetwork | undefined;
   requestingCumulativeUsageByNetwork: boolean;
   receivedCumulativeUsageByNetworkError: AjaxError | undefined;
 
   totalPacketLossByLink: TotalPacketLossByLink | undefined;
   requestingTotalPacketLossByLink: boolean;
   receivedTotalPacketLossByLinkError: AjaxError | undefined;
 
   packetLossByLink: PacketLossByLink | undefined;
   requestingPacketLossByLink: boolean;
   receivedPacketLossByLinkError: AjaxError | undefined;

   serviceAvailability: ServiceAvailability | undefined;
   requestingServiceAvailability: boolean;
   receivedServiceAvailabilityError: AjaxError | undefined;
 }
 
 export const initialDataUsageState: State = {
   totalUsageByNetwork: undefined,
   requestingTotalUsageByNetwork: false,
   receivedTotalUsageByNetworkError: undefined,
 
   totalUsageByLink: undefined,
   requestingTotalUsageByLink: false,
   receivedTotalUsageByLinkError: undefined,
 
   usageByLink: undefined,
   requestingUsageByLink: false,
   receivedUsageByLinkError: undefined,
 
   cumulativeUsageByLink: undefined,
   requestingCumulativeUsageByLink: false,
   receivedCumulativeUsageByLinkError: undefined,
 
   usageByNetwork: undefined,
   requestingUsageByNetwork: false,
   receivedUsageByNetworkError: undefined,
 
   cumulativeUsageByNetwork: undefined,
   requestingCumulativeUsageByNetwork: false,
   receivedCumulativeUsageByNetworkError: undefined,
 
   totalPacketLossByLink: undefined,
   requestingTotalPacketLossByLink: false,
   receivedTotalPacketLossByLinkError: undefined,
 
   packetLossByLink: undefined,
   requestingPacketLossByLink: false,
   receivedPacketLossByLinkError: undefined,

   serviceAvailability: undefined,
   requestingServiceAvailability: false,
   receivedServiceAvailabilityError: undefined
 };
 
 // Reducer
 export default function reducer(state: State = initialDataUsageState, action: Action): State {
   let partialState: Partial<State> | undefined;
   switch (action.type) {
     case ActionCreators.RequestTotalUsageByNetwork.type:
       partialState = {
         requestingTotalUsageByNetwork: true,
         receivedTotalUsageByNetworkError: undefined,
       };
       break;
     case ActionCreators.ReceivedTotalUsageByNetworkSuccess.type:
       partialState = {
         totalUsageByNetwork: action.payload,
         requestingTotalUsageByNetwork: false,
         receivedTotalUsageByNetworkError: undefined,
       };
       break;
     case ActionCreators.ReceivedTotalUsageByNetworkError.type:
       partialState = {
         requestingTotalUsageByNetwork: false,
         receivedTotalUsageByNetworkError: action.payload,
       };
       break;
     case ActionCreators.ClearReceivedTotalUsageByNetworkError.type:
       partialState = {
         receivedTotalUsageByNetworkError: undefined,
       };
       break;
     case ActionCreators.RequestTotalUsageByLink.type:
       partialState = {
         requestingTotalUsageByLink: true,
         receivedTotalUsageByLinkError: undefined,
       };
       break;
     case ActionCreators.ReceivedTotalUsageByLinkSuccess.type:
       partialState = {
         totalUsageByLink: action.payload,
         requestingTotalUsageByLink: false,
         receivedTotalUsageByLinkError: undefined,
       };
       break;
     case ActionCreators.ReceivedTotalUsageByLinkError.type:
       partialState = {
         requestingTotalUsageByLink: false,
         receivedTotalUsageByLinkError: action.payload,
       };
       break;
     case ActionCreators.ClearReceivedTotalUsageByLinkError.type:
       partialState = {
         receivedTotalUsageByLinkError: undefined,
       };
       break;
 
     case ActionCreators.RequestUsageByLink.type:
       partialState = {
         requestingUsageByLink: true,
         receivedUsageByLinkError: undefined,
       };
       break;
 
     case ActionCreators.ReceivedUsageByLinkSuccess.type:
       partialState = {
         usageByLink: action.payload,
         requestingUsageByLink: false,
         receivedUsageByLinkError: undefined,
       };
       break;
     case ActionCreators.ReceivedUsageByLinkError.type:
       partialState = {
         requestingUsageByLink: false,
         receivedUsageByLinkError: action.payload,
       };
       break;
     case ActionCreators.ClearReceivedUsageByLinkError.type:
       partialState = {
         receivedUsageByLinkError: undefined,
       };
       break;
 
     case ActionCreators.ReceivedUsageByLinkCumulativeSuccess.type:
       partialState = {
         cumulativeUsageByLink: action.payload,
         requestingCumulativeUsageByLink: false,
         receivedCumulativeUsageByLinkError: undefined,
       };
       break;
     case ActionCreators.ReceivedUsageByLinkCumulativeError.type:
       partialState = {
         requestingCumulativeUsageByLink: false,
         receivedCumulativeUsageByLinkError: action.payload,
       };
       break;
     case ActionCreators.ClearReceivedUsageByLinkCumulativeError.type:
       partialState = {
         receivedCumulativeUsageByLinkError: undefined,
       };
       break;
 
     case ActionCreators.RequestUsageByNetwork.type:
       partialState = {
         requestingUsageByNetwork: true,
         receivedUsageByNetworkError: undefined,
       };
       break;
     case ActionCreators.ReceivedUsageByNetworkSuccess.type:
       partialState = {
         usageByNetwork: action.payload,
         requestingUsageByNetwork: false,
         receivedUsageByNetworkError: undefined,
       };
       break;
     case ActionCreators.ReceivedUsageByNetworkError.type:
       partialState = {
         requestingUsageByNetwork: false,
         receivedUsageByNetworkError: action.payload,
       };
       break;
     case ActionCreators.ClearReceivedUsageByNetworkError.type:
       partialState = {
         receivedUsageByNetworkError: undefined,
       };
       break;
 
     case ActionCreators.RequestCumulativeUsageByNetwork.type:
       partialState = {
         requestingCumulativeUsageByNetwork: true,
         receivedCumulativeUsageByNetworkError: undefined,
       };
       break;
     case ActionCreators.ReceivedCumulativeUsageByNetworkSuccess.type:
       partialState = {
         cumulativeUsageByNetwork: action.payload,
         requestingCumulativeUsageByNetwork: false,
         receivedCumulativeUsageByNetworkError: undefined,
       };
       break;
     case ActionCreators.ReceivedCumulativeUsageByNetworkError.type:
       partialState = {
         requestingCumulativeUsageByNetwork: false,
         receivedCumulativeUsageByNetworkError: action.payload,
       };
       break;
     case ActionCreators.ClearReceivedCumulativeUsageByNetworkError.type:
       partialState = {
         receivedCumulativeUsageByNetworkError: undefined,
       };
       break;
 
     case ActionCreators.RequestTotalPacketLossByLink.type:
       partialState = {
         requestingTotalPacketLossByLink: true,
         receivedTotalPacketLossByLinkError: undefined,
       };
       break;
     case ActionCreators.ReceivedTotalPacketLossByLinkSuccess.type:
       partialState = {
         totalPacketLossByLink: action.payload,
         requestingTotalPacketLossByLink: false,
         receivedTotalPacketLossByLinkError: undefined,
       };
       break;
     case ActionCreators.ReceivedTotalPacketLossByLinkError.type:
       partialState = {
         requestingTotalPacketLossByLink: false,
         receivedTotalPacketLossByLinkError: action.payload,
       };
       break;
     case ActionCreators.ClearReceivedTotalPacketLossByLinkError.type:
       partialState = {
         receivedTotalPacketLossByLinkError: undefined,
       };
       break;
     
     case ActionCreators.RequestServiceAvailability.type:
       partialState = {
         requestingServiceAvailability: true,
         receivedServiceAvailabilityError: undefined,
       };
       break;
     case ActionCreators.ReceivedServiceAvailabilitySuccess.type:
       partialState = {
         serviceAvailability: action.payload,
         requestingServiceAvailability: false,
         receivedServiceAvailabilityError: undefined,
       };
       break;
     case ActionCreators.ReceivedServiceAvailabilityError.type:
       partialState = {
         requestingServiceAvailability: false,
         receivedServiceAvailabilityError: action.payload,
       };
       break;
     case ActionCreators.ClearReceivedServiceAvailabilityError.type:
       partialState = {
         receivedServiceAvailabilityError: undefined,
       };
       break;

       
      
     case ActionCreators.ClearDataUsage.type: {
       partialState = {
         totalUsageByNetwork: undefined,
         usageByNetwork: undefined,
         cumulativeUsageByNetwork: undefined,
         usageByLink: undefined,
         cumulativeUsageByLink: undefined,
         totalUsageByLink: undefined,
         packetLossByLink: undefined,
         totalPacketLossByLink: undefined,
         serviceAvailability: undefined,
       };
       break;
     }
     default:
       return state;
   }
   return { ...state, ...partialState };
 }